/**
 * Level 2 — one outcome. Back link, a header card, then ONE card holding the
 * chart region, the scope line + detriment mix, and records-by-band, separated
 * by full-bleed rules (they all describe the same selected period — SPEC §6).
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';

import type { FilterConfig } from '../../../records/filter';
import { BAND_COLORS, bandOf } from '../constants';
import { useOutcomeDetail } from '../hooks';
import type { BandRecords, Framework, Grain, Plot } from '../types';
import { fmt, recordsLabel } from '../utils';
import { BandTiles } from './BandTiles';
import { DetrimentColumns } from './DetrimentColumns';
import { OutcomeChart } from './OutcomeChart';
import { BandGauge, BandHistogram, Delta } from './parts';

const SEPARATOR = '-mx-4 my-[1.15rem] border-t border-border';

export function DetailView({
  datasetId,
  framework,
  filters,
  outcomeId,
  grain,
  allowedGrains,
  plot,
  selectedBucket,
  periodLabel,
  onGrainChange,
  onPlotChange,
  onSelectBucket,
  onBack,
}: {
  datasetId: string;
  framework: Framework;
  filters: FilterConfig;
  outcomeId: string;
  grain: Grain;
  allowedGrains: Grain[];
  plot: Plot;
  selectedBucket: string | null;
  periodLabel: string;
  onGrainChange: (g: Grain) => void;
  onPlotChange: (p: Plot) => void;
  onSelectBucket: (b: string | null) => void;
  onBack: () => void;
}) {
  const { data, isLoading, isFetching, isError } = useOutcomeDetail(
    datasetId,
    framework,
    outcomeId,
    grain,
    filters,
  );

  const scopedPoint = useMemo(
    () => (selectedBucket ? data?.series.find((p) => p.bucketStart === selectedBucket) ?? null : null),
    [data, selectedBucket],
  );

  // Each band's records respect the chart selection (SPEC §6.2.4): when a bucket
  // is scoped, keep only records whose date falls in [bucketStart, next).
  const bandRecords = useMemo<BandRecords>(() => {
    const empty: BandRecords = { low: [], mod: [], high: [] };
    if (!data) return empty;
    if (!selectedBucket) return data.bandRecords;
    const idx = data.series.findIndex((p) => p.bucketStart === selectedBucket);
    if (idx < 0) return data.bandRecords;
    const start = data.series[idx].bucketStart;
    const end = data.series[idx + 1]?.bucketStart; // exclusive; undefined on the last bucket
    const inBucket = (r: { occurredAt: string | null }) =>
      r.occurredAt !== null && r.occurredAt >= start && (end === undefined || r.occurredAt < end);
    return {
      low: data.bandRecords.low.filter(inBucket),
      mod: data.bandRecords.mod.filter(inBucket),
      high: data.bandRecords.high.filter(inBucket),
    };
  }, [data, selectedBucket]);

  // The selected bucket's [start, end) window (ISO dates) — passed to BandTiles
  // so the paginated "View more" fetch is scoped to the same bucket as the
  // sample above (end undefined on the last bucket → no upper bound).
  const bucketWindow = useMemo<{ start: string; end: string | null } | null>(() => {
    if (!selectedBucket || !data) return null;
    const idx = data.series.findIndex((p) => p.bucketStart === selectedBucket);
    if (idx < 0) return null;
    return { start: data.series[idx].bucketStart, end: data.series[idx + 1]?.bucketStart ?? null };
  }, [data, selectedBucket]);

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-1 text-xs font-bold text-[var(--go-good-vibes)] hover:underline"
      >
        ← All outcomes
      </button>

      {isError ? (
        <div className="rounded-[var(--radius)] border border-border bg-background p-8 text-center text-destructive">
          Failed to load outcome detail. Please try again.
        </div>
      ) : isLoading || !data ? (
        <DetailSkeleton />
      ) : (
        // Background refetch (grain/filter change) over cached data: dim + a
        // "Refreshing" pill, matching the overview, so stale figures don't sit
        // silently on screen.
        <div className="relative">
          {isFetching && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center">
              <div className="flex items-center gap-2 rounded-full border border-border bg-background/95 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-[var(--shadow-card)]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Refreshing…
              </div>
            </div>
          )}
          <div
            className={`transition-opacity ${isFetching ? 'opacity-50' : 'opacity-100'}`}
            aria-busy={isFetching}
          >
          {/* header */}
          <div className="mb-5 grid grid-cols-1 items-center gap-6 rounded-[var(--radius)] border border-border bg-background px-5 py-5 md:grid-cols-[1.6fr_1fr]">
            <div>
              <div className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">
                Outcome
              </div>
              <div className="text-lg font-extrabold leading-tight tracking-[-0.01em]">
                {data.outcome.name}
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span
                  className="text-[2.5rem] font-extrabold leading-none tracking-[-0.04em]"
                  style={{
                    color:
                      data.outcome.volume > 0
                        ? BAND_COLORS[bandOf(data.outcome.score)]
                        : 'hsl(var(--muted-foreground))',
                  }}
                >
                  {data.outcome.volume > 0 ? data.outcome.score.toFixed(1) : '—'}
                </span>
                <Delta cur={data.outcome.score} prev={data.outcome.scorePrev} dp={1} />
              </div>
              <BandGauge score={data.outcome.score} />
            </div>
            <div className="border-t border-border pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
              <div className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">
                Records by band
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-[2rem] font-extrabold leading-none tracking-[-0.035em]">
                  {fmt(data.outcome.volume)}
                </span>
                <Delta cur={data.outcome.volume} prev={data.outcome.volumePrev} pct neutral />
              </div>
              <div className="mt-2">
                <BandHistogram
                  bands={data.outcome.bands}
                  max={Math.max(1, data.outcome.bands.low, data.outcome.bands.mod, data.outcome.bands.high)}
                  height={46}
                />
              </div>
            </div>
          </div>

          {/* main card */}
          <div className="rounded-[var(--radius)] border border-border bg-background p-4">
            <OutcomeChart
              series={data.series}
              grain={grain}
              allowedGrains={allowedGrains}
              plot={plot}
              selectedBucket={selectedBucket}
              periodLabel={periodLabel}
              onGrainChange={onGrainChange}
              onPlotChange={onPlotChange}
              onSelectBucket={onSelectBucket}
            />

            <div className={SEPARATOR} />

            {/* scope line */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <span className="flex flex-wrap items-baseline gap-3 text-[13px] font-extrabold">
                {scopedPoint ? scopedPoint.label : 'Entire period'}
                <span
                  className="rounded-full bg-muted px-2 py-0.5 text-xs font-extrabold"
                  style={{
                    color: BAND_COLORS[bandOf(scopedPoint ? scopedPoint.score : data.outcome.score)],
                  }}
                >
                  Conduct score {(scopedPoint ? scopedPoint.score : data.outcome.score).toFixed(1)}
                </span>
                <span className="text-[11px] font-semibold text-muted-foreground">
                  {recordsLabel(scopedPoint ? scopedPoint.volume : data.outcome.volume)}
                </span>
              </span>
              {selectedBucket ? (
                <button
                  type="button"
                  onClick={() => onSelectBucket(null)}
                  className="rounded-full border border-border bg-background px-2.5 py-1 text-[10px] font-bold text-[var(--go-good-vibes)] hover:border-[var(--go-good-vibes)]"
                >
                  Clear ×
                </button>
              ) : (
                <span className="text-[10px] font-semibold text-muted-foreground">
                  Click a point on the chart to scope this
                </span>
              )}
            </div>

            <div className="mt-4">
              <DetrimentColumns detail={data} selectedBucket={selectedBucket} />
            </div>

            <div className={SEPARATOR} />

            <BandTiles
              bands={scopedPoint ? scopedPoint.bands : data.outcome.bands}
              bandRecords={bandRecords}
              datasetId={datasetId}
              framework={framework}
              outcomeId={outcomeId}
              filters={filters}
              bucketStart={bucketWindow?.start ?? null}
              bucketEnd={bucketWindow?.end ?? null}
            />
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <div className="h-[9.5rem] animate-pulse rounded-[var(--radius)] border border-border bg-muted/40" />
      <div className="h-[32rem] animate-pulse rounded-[var(--radius)] border border-border bg-muted/40" />
    </div>
  );
}
